import styled, { css } from "styled-components";


export const DashBoardContainer = styled.div`
  min-height: 100vh;
  background-color: ${({ bgColor, theme }) => bgColor || theme.card.bgColor};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: ${({ gap }) => gap || "20px"};
  padding: 20px;
  border-radius: ${({ borderRadius }) => borderRadius || "16px 16px 0 0"};
  margin-top: ${({ marginTop }) => marginTop || "32px"};
`;

export const DashBoardCommonGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || "1fr 1fr"};
  gap: ${({ gap }) => gap || "20px"};
  justify-content: ${({ justifyContent }) => justifyContent || `space-between`};
  align-content: ${({ alignContent }) => alignContent};
  align-items: ${({ alignItem }) => alignItem};
  background-color: ${({ bgColor }) => bgColor};
  /* ✅ Responsive breakpoint controlled by props */
  ${({ responsiveBreakpoint, responsiveColumns }) =>
    responsiveBreakpoint &&
    responsiveColumns &&
    css`
      @media screen and (max-width: ${responsiveBreakpoint}) {
        grid-template-columns: ${responsiveColumns};
      }
    `}
`;

// Reusable Dashboard Card Container - matches Figma design exactly
export const DashboardStatCardContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 8px;
  width: 100%;
  min-height: 96px;
  background-color: ${({ theme }) => theme?.colors?.main?.white || "#ffffff"};
  border: 1px solid
    ${({ theme }) => theme?.colors?.border?.tableBorder || "#d3e0fc"};
  border-radius: 8px;
  flex: none;
  /* &:hover {
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
    border-color: #c0d0ce;
  } */

  @media screen and (max-width: 768px) {
    padding: 14px;
  }

  @media screen and (max-width: 500px) {
    padding: 12px;
  }
`;

// Card Header Section - matches Figma Frame 2087324740
export const DashboardStatCardHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 12px;
  width: 100%;
  height: 24px;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
`;

// Card Title - matches Figma text (e.g., "Invoice Raised Today", "Job Created Today")
export const DashboardStatCardTitle = styled.span`
  margin: 0;
  width: auto;
  height: 17px;
  font-family: "Mona Sans", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: ${({ theme }) => theme?.colors?.main?.labelText || "#353a46"};
  flex: none;
  order: 0;
  flex-grow: 0;
  white-space: nowrap;

  @media screen and (max-width: 500px) {
    font-size: 11px;
  }
`;

// Icon Container - matches Figma Frame 39322
export const DashboardStatCardIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 0px;
  gap: 4px;
  margin: 0;
  width: 24px;
  height: 24px;
  background-color: #e9f0fe;
  border-radius: 4px;
  flex: none;
  order: 1;
  flex-grow: 0;
  flex-shrink: 0;
  cursor: pointer;

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
    flex: none;
    flex-grow: 0;
    transition: all 0.2s ease;
  }

  svg {
    width: 24px;
    height: 24px;
    flex: none;
    flex-grow: 0;
  }
  &:hover {
    background-color: #B4C6F9;
    transition: background-color 0.2s ease;
  }

`;

// Card Content Section - matches Figma Frame 39308
export const DashboardStatCardContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  padding: 0px;
  gap: 4px;
  width: 100%;
  height: 32px;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
`;

// Main Stat Value - matches Figma text (e.g., "23", "75")
export const DashboardStatCardValue = styled.span`
  width: auto;
  height: 32px;
  font-family: "Mona Sans", sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 32px;
  line-height: 100%;
  color: ${({ theme }) => theme?.colors?.main?.textMain || "#000e2a"};
  flex: none;
  order: 0;
  flex-grow: 1;

  @media screen and (max-width: 768px) {
    font-size: 28px;
    line-height: 28px;
  }

  @media screen and (max-width: 500px) {
    font-size: 24px;
    line-height: 24px;
  }
`;

// Secondary Stat Text - matches Figma text (e.g., "6 Pending", "32 Visited")
export const DashboardStatCardSecondary = styled.span`
  width: auto;
  height: 17px;
  font-family: "Mona Sans", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: ${({ theme }) => theme?.colors?.main?.labelText || "#353a46"};
  flex: none;
  order: 1;
  flex-grow: 0;
  white-space: nowrap;

  @media screen and (max-width: 500px) {
    font-size: 11px;
  }
`;

// Skeleton Loading Components
const shimmerAnimation = css`
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  animation: shimmer 2s infinite;
  background: linear-gradient(
    to right,
    #f0f0f0 0%,
    #e0e0e0 20%,
    #f0f0f0 40%,
    #f0f0f0 100%
  );
  background-size: 1000px 100%;
`;

export const DashboardStatCardSkeletonTitle = styled.div`
  width: 120px;
  height: 17px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex: none;
  order: 0;
  flex-grow: 0;
`;

export const DashboardStatCardSkeletonIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex: none;
  order: 1;
  flex-grow: 0;
  flex-shrink: 0;
`;

export const DashboardStatCardSkeletonValue = styled.div`
  width: 80px;
  height: 32px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex: none;
  order: 0;
  flex-grow: 0;

  @media screen and (max-width: 768px) {
    height: 28px;
  }

  @media screen and (max-width: 500px) {
    height: 24px;
  }
`;

export const DashboardStatCardSkeletonSecondary = styled.div`
  width: 60px;
  height: 17px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex: none;
  order: 1;
  flex-grow: 0;
`;

// TodaysAttendance Skeleton Components
export const TodaysAttendanceSkeletonTitle = styled.div`
  width: 140px;
  height: 20px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex: 1;
`;

export const TodaysAttendanceSkeletonIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex-shrink: 0;
`;

export const TodaysAttendanceSkeletonCircle = styled.div`
  position: absolute;
  border-radius: 50%;
  ${shimmerAnimation}
`;

export const TodaysAttendanceSkeletonLegend = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  ${shimmerAnimation}
  width: 70px;
  height: 24px;
`;

// TopPerformers Skeleton Components
export const TopPerformersSkeletonTitle = styled.div`
  width: 120px;
  height: 20px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex: 1;
`;

export const TopPerformersSkeletonSelect = styled.div`
  width: 100px;
  height: 24px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex-shrink: 0;
`;

export const TopPerformersSkeletonRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 32px;
`;

export const TopPerformersSkeletonRankBadge = styled.div`
  width: 16px;
  height: 20px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex-shrink: 0;
`;

export const TopPerformersSkeletonAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  ${shimmerAnimation}
  flex-shrink: 0;
`;

export const TopPerformersSkeletonName = styled.div`
  width: 120px;
  height: 16px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex: 1;
  min-width: 0;
`;

export const TopPerformersSkeletonStats = styled.div`
  width: 80px;
  height: 16px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex-shrink: 0;
`;

// TopCustomers Skeleton Components (reuses some TopPerformers skeletons)
export const TopCustomersSkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 16px 3fr 1fr 1fr;
  align-items: center;
  padding: 0px;
  gap: 12px;
  width: 100%;
  height: 32px;
`;

export const TopCustomersSkeletonSpent = styled.div`
  width: 70px;
  height: 17px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex-shrink: 0;
`;

// HourlyEstimatedVsCompletedChart Skeleton Components
export const HourlySkeletonTitle = styled.div`
  width: 200px;
  height: 20px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex: 1;
`;

export const HourlySkeletonSelect = styled.div`
  width: 100px;
  height: 24px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex-shrink: 0;
`;

export const HourlySkeletonLegend = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  width: 100%;
`;

export const HourlySkeletonLegendItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const HourlySkeletonLegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  ${shimmerAnimation}
  flex-shrink: 0;
`;

export const HourlySkeletonLegendText = styled.div`
  width: 100px;
  height: 17px;
  border-radius: 4px;
  ${shimmerAnimation}
`;

export const HourlySkeletonChart = styled.div`
  position: relative;
  width: 100%;
  height: 172px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px 0;
`;

export const HourlySkeletonChartBars = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 100%;
  padding: 0 8px;
`;

export const HourlySkeletonBarGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 4px;
  max-width: 48px;
`;

export const HourlySkeletonBar = styled.div`
  flex: 1;
  border-radius: 0;
  ${shimmerAnimation}
  min-height: 20px;
`;

export const HourlySkeletonBarLabel = styled.div`
  width: 40px;
  height: 14px;
  border-radius: 4px;
  ${shimmerAnimation}
  margin: 8px auto 0;
`;

// JobsStatusBarChart Skeleton Components
export const JobsStatusSkeletonChartContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 173px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 12px 0;
`;

export const JobsStatusSkeletonChartBars = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  height: 100%;
  padding: 0 8px;
`;

export const JobsStatusSkeletonBar = styled.div`
  flex: 1;
  border-radius: 0;
  ${shimmerAnimation}
  min-height: 20px;
  max-width: 48px;
`;

export const JobsStatusSkeletonBarLabel = styled.div`
  width: 60px;
  height: 14px;
  border-radius: 4px;
  ${shimmerAnimation}
  margin: 8px auto 0;
`;

// RevenueCompareChart Skeleton Components
export const RevenueSkeletonTitle = styled.div`
  width: 180px;
  height: 20px;
  border-radius: 4px;
  ${shimmerAnimation}
`;

export const RevenueSkeletonLegend = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

export const RevenueSkeletonLegendItem = styled.div`
  width: 57px;
  height: 20px;
  border-radius: 4px;
  ${shimmerAnimation}
`;

export const RevenueSkeletonChart = styled.div`
  width: 100%;
  height: 196px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px 0;

  @media (max-width: 1024px) {
    height: 180px;
  }

  @media (max-width: 768px) {
    height: 160px;
  }

  @media (max-width: 480px) {
    height: 140px;
  }
`;

export const RevenueSkeletonChartLine = styled.div`
  position: absolute;
  width: ${({ width }) => width || '90%'};
  height: ${({ height }) => height || 'max-content'};
  border-radius: 2px;
  ${shimmerAnimation}
  top: ${({ top }) => top || '50%'};
  left: ${({ left }) => left || '5%'};
  transform: ${({ transform }) => transform || 'none'};
`;

export const RevenueSkeletonChartGrid = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 12px 8px;
  position: relative;
`;

export const RevenueSkeletonChartGridLine = styled.div`
  width: 100%;
  height: 1px;
  border-radius: 1px;
  ${shimmerAnimation}
  opacity: 0.2;
`;

// SmallInfoCard Skeleton Components
export const SmallInfoCardSkeletonIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  ${shimmerAnimation}
  flex-shrink: 0;
`;

export const SmallInfoCardSkeletonTitle = styled.div`
  width: 100px;
  height: 17px;
  border-radius: 4px;
  ${shimmerAnimation}
`;

export const SmallInfoCardSkeletonAmount = styled.div`
  width: 80px;
  height: 24px;
  border-radius: 4px;
  ${shimmerAnimation}
`;

export const SmallInfoCardSkeletonDelta = styled.div`
  width: 50px;
  height: 17px;
  border-radius: 4px;
  ${shimmerAnimation}
  min-width: 72px;
`;

export const SmallInfoCardSkeletonSubText = styled.div`
  width: 60px;
  height: 17px;
  border-radius: 4px;
  ${shimmerAnimation}
`;

// Today's Attendance Section 
export const TodaysAttendanceContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 16px;
  width: 100%;
  min-width: 0;
  background: #ffffff;
  border: 1px solid #d3e0fc;
  border-radius: 8px;

  @media screen and (max-width: 768px) {
    padding: 14px;
    gap: 14px;
  }

  @media screen and (max-width: 500px) {
    padding: 12px;
    gap: 12px;
  }
`;

// Attendance Header - matches Figma Frame 39320
export const TodaysAttendanceHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 12px;
  width: 100%;
  height: 32px;
  background: #e9f0fe;
  border-radius: 4px;
  padding: 6px 8px;
  box-sizing: border-box;
`;

// Attendance Title
export const TodaysAttendanceTitle = styled.span`
  font-family: "Mona Sans", sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 19.74px;
  color: #000e2a;
  flex: 1;
`;

// Attendance Icon Button - matches Figma Frame 39322
export const TodaysAttendanceIconButton = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  background: #ffffff;
  border-radius: 4px;
  flex-shrink: 0;
  cursor: pointer;

  img,
  svg {
    width: 24px;
    height: 24px;
  }
`;

// Attendance Chart Container
export const TodaysAttendanceChartContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 196px;
  height: 183px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  margin-top: 16px;

  @media screen and (max-width: 500px) {
    max-width: 160px;
    height: 183px;
  }
`;

// Concentric Circle - for the attendance chart
export const AttendanceCircle = styled.div`
  position: absolute;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Mona Sans", sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
  color: #000e2a;
  text-align: center;
`;

// Attendance Legend Item - matches Figma Frame 39343, 39348, 39349, 39350
export const AttendanceLegendItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 12px;
  gap: 6px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #ffffff;
  border-radius: 32px;
  position: absolute;
  white-space: nowrap;
  z-index: 20;
`;

// Legend Dot
export const AttendanceLegendDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

// Legend Text
export const AttendanceLegendText = styled.span`
  font-family: "Mona Sans", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 12px;
  color: #000e2a;
`;

// Top Performers Section - matches Figma Frame 39316
export const TopPerformersContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 16px;
  width: 100%;
  min-width: 0;
  background: #ffffff;
  border: 1px solid #d3e0fc;
  border-radius: 8px;

  @media screen and (max-width: 768px) {
    padding: 14px;
    gap: 14px;
  }

  @media screen and (max-width: 500px) {
    padding: 12px;
    gap: 12px;
  }
`;

// Top Performers Header - matches Figma Frame 39320
export const TopPerformersHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 12px;
  width: 100%;
  height: 32px;
  background: #e9f0fe;
  border-radius: 4px;
  padding: 6px 8px;
  box-sizing: border-box;
`;

// Top Performers Title
export const TopPerformersTitle = styled.span`
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 19.74px;
  color: #000e2a;
  flex: 1;
`;

// Custom Select Container - matches Figma Frame 39322
export const TopPerformersSelectContainer = styled.div`
  display: flex;
  box-sizing: border-box;
  flex-direction: row;
  align-items: center;
  padding: 4px 8px;
  gap: 6px;
  width: auto;
  height: 25px;
  background: #ffffff;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;

  border: 1px solid transparent;

  &:hover {
    border: 1px solid #b4c6f9;
  }
`;

// Custom Select Text
export const TopPerformersSelectText = styled.span`
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16.92px;
  color: #353a46;
  white-space: nowrap;
`;

// Custom Select Icon
export const TopPerformersSelectIcon = styled.img`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
`;

// Dropdown Container - matches Figma Frame 2087324842
export const TopPerformersDropdownContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px;
  gap: 4px;
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: #ffffff;
  border: 1px solid #d3e0fc;
  box-shadow: 0px 4px 20px rgba(34, 102, 239, 0.2);
  border-radius: 4px;
  z-index: 100;
  min-width: 100%;
`;

// Dropdown Option - matches Figma Frames 2087324743, 2087324744, etc.
export const TopPerformersDropdownOption = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 8px;
  width: 80px;
  height: 28px;
  background: ${({ isSelected }) => isSelected ? '#E9F0FE' : '#FFFFFF'};
  border-radius: 4px;
  cursor: pointer;
  font-style: normal;
  font-weight: ${({ isSelected }) => isSelected ? 700 : 400};
  font-size: 12px;
  line-height: 17px;
  color: ${({ isSelected }) => isSelected ? '#000E2A' : '#353A46'};
  white-space: nowrap;
  flex: none;
  order: 0;
  flex-grow: 0;

  &:hover {
    background: #f3f5f9;
  }
`;

// Performers List Container
export const TopPerformersListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

// Performer Row - matches Figma Frame 39314, 39315, etc.
export const TopPerformerRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 12px;
  width: 100%;
  height: 32px;
`;

export const TopCustomerRow = styled.div`
  display: grid;
  grid-template-columns: 16px 3fr 1fr 1fr;
  align-items: center;
  padding: 0px;
  gap: 12px;
  width: 100%;
  height: 32px;
`;

// Rank Badge - matches Figma Group 2085662829 (15.96px x 20px)
export const TopPerformerRankBadge = styled.div`
  position: relative;
  width: 16px;
  height: 20px;
  min-width: 16px;
  min-height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Rank Badge SVG Image
export const TopPerformerRankBadgeImage = styled.img`
  width: 16px;
  height: 20px;
  position: absolute;
  top: 0;
  left: 0;
`;

// Rank Badge Text - positioned absolutely over the SVG
export const TopPerformerRankText = styled.span`
  position: absolute;
  top: 35%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-style: normal;
  font-weight: 700;
  font-size: 10px;
  line-height: 7px;
  color: #ffffff;
  text-align: center;
  z-index: 1;
  pointer-events: none;
`;

// Performer Info Container - matches Figma Frame 39310
export const TopPerformerInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

// Performer Stats Container - matches Figma Frame 39311
export const TopPerformerStatsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: auto;
  height: 32px;
  flex-shrink: 0;
`;

// Performer Stats Text
export const TopPerformerStatsText = styled.span`
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16.92px;
  color: #353a46;
  white-space: nowrap; 
`;

export const TopCustomerSpentStatsText = styled.span`
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 17px;
  color: #000e2a;
  white-space: nowrap;
`;

// Jobs Status Section - matches Figma Frame 39341
export const JobsStatusContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 16px;
  width: 100%;
  min-width: 0;
  background: #ffffff;
  border: 1px solid #d3e0fc;
  border-radius: 8px;

  @media screen and (max-width: 768px) {
    padding: 14px;
    gap: 14px;
  }

  @media screen and (max-width: 500px) {
    padding: 12px;
    gap: 12px;
  }
`;

// Jobs Status Chart Container - matches Figma Group 29696
export const JobsStatusChartContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 200px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

// Chart Wrapper - contains the actual chart
export const JobsStatusChartWrapper = styled.div`
  position: relative;
  flex: 1;
  height: 100%;
  display: flex;
  align-items: stretch;
  padding-left: 0;
  padding-bottom: 0;
  overflow: visible;

  > div {
    width: 100%;
    height: 100%;
  }

  canvas {
    min-height: 173px !important;
    height: 100% !important;
  }
`;

// ============================================
// Hourly Estimated vs Completed Chart Styles
// ============================================

// Container for the entire component
export const HourlyEstimatedVsCompletedContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 12px;
  width: 100%;
  background: #ffffff;
  border: 1px solid #d3e0fc;
  border-radius: 8px;
  flex: none;

  @media screen and (max-width: 768px) {
    padding: 14px;
    gap: 14px;
  }

  @media screen and (max-width: 500px) {
    padding: 12px;
    gap: 12px;
  }
`;

// Header section with title and dropdown
export const HourlyEstimatedVsCompletedHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 12px;
  width: 100%;
  height: 32px;
  background: #e9f0fe;
  border-radius: 4px;
  padding: 6px 8px;
  box-sizing: border-box;
`;

// Title for the section
export const HourlyEstimatedVsCompletedTitle = styled.span`
  font-family: "Mona Sans", sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 19.74px;
  color: #000e2a;
  flex: 1;
`;

// Dropdown selector
export const HourlyEstimatedVsCompletedSelect = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 8px;
  gap: 6px;
  width: auto;
  height: 24px;
  background: #ffffff;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  border: 1px solid transparent;

  &:hover {
    border: 1px solid #b4c6f9;
  }
`;

// Dropdown text
export const HourlyEstimatedVsCompletedSelectText = styled.span`
  font-family: "Mona Sans", sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16.92px;
  color: #353a46;
  white-space: nowrap;
`;

// Dropdown icon
export const HourlyEstimatedVsCompletedSelectIcon = styled.img`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
`;

// Legend container
export const HourlyEstimatedVsCompletedLegend = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  width: 100%;
  padding: 0;
  margin-top: 0px;
`;

// Individual legend item
export const HourlyEstimatedVsCompletedLegendItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

// Legend dot
export const HourlyEstimatedVsCompletedLegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

// Legend text
export const HourlyEstimatedVsCompletedLegendText = styled.span`
  font-family: "Mona Sans", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16.92px;
  color: #353a46;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const HourlyEstimatedVsCompletedLegendValue = styled.span`
  font-family: "Mona Sans", sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 16.92px;
  color: #353a46;
`;

// Chart container
export const HourlyEstimatedVsCompletedChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 172px;
  margin-top: 0px;
`;

// Chart wrapper
export const HourlyEstimatedVsCompletedChartWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  canvas {
    width: 100% !important;
    height: 100% !important;
  }
`;